import React, { useState, useMemo } from 'react';
import { X, Euro, Timer, Award, MapPin, Sparkles, ArrowRight, Brain, Briefcase, Zap, Eye } from 'lucide-react';
import { localize } from '../../data/i18nData';
import { formatMoney } from '../../utils/format';
import {
  argumentationOutcome,
  getClientDriversForContract,
  canSkipArgumentation,
  pickArgumentChoices,
} from '../../utils/argumentation';
import { generateClientTells } from '../../utils/clientTells';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import { resolveClient } from '../../utils/resolveClient';
import ClientLogo from '../crm/ClientLogo';

const ARG_META = {
  prix:       { Icon: Euro,   label: 'Prix le plus compétitif', sub: 'Marges réduites, attractif pour les budgets serrés' },
  délais:     { Icon: Timer,  label: 'Délais courts garantis',  sub: 'Engagement de livraison stricte, primauté du temps' },
  qualité:    { Icon: Award,  label: 'Qualité de service',      sub: 'Process, suivi proactif, gestion des incidents' },
  proximité:  { Icon: MapPin, label: 'Proximité géographique',   sub: 'Implantation locale, réactivité, accompagnement' },
};

/**
 * ContractArgumentationModal — Phase 3 bis (03/06/2026).
 *
 * Mini-jeu déclenché quand le joueur clique "Accepter" sur un contrat.
 *
 * Sans support commercial :
 *   Modale obligatoire. Choisir 1 argument parmi 3 (le bon = +5%,
 *   le secondaire = 0%, le mauvais = −5% remise concédée).
 *
 * Avec support commercial (Léa / Marc / Sylvie) :
 *   Modale optionnelle. 2 choix : "Accepter directement" (base) ou
 *   "Argumenter (opt-in)" pour tenter le bonus.
 *
 * En sortie : `onConfirm(priceMultiplier, outcome)` — la suite du flux
 * d'acceptation (assignation véhicule/chauffeur) se fait dans
 * ContractAssignModal en aval.
 */
export default function ContractArgumentationModal({ contract, onClose, onConfirm }) {
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const clientLoyalty = useGameStore(s => s.clientLoyalty || EMPTY_OBJECT);
  const skipAllowed = canSkipArgumentation(hiredSupport);
  const c = useMemo(() => contract ? localize('contract', contract) : null, [contract]);
  const client = c ? resolveClient(c) : null;
  const drivers = c ? getClientDriversForContract(client, c) : null;
  const loyaltyData = client ? clientLoyalty[client.id] : null;
  const tells = useMemo(
    () => (c ? generateClientTells(client, c, loyaltyData) : []),
    [c, client, loyaltyData],
  );
  const choices = useMemo(
    () => (drivers ? pickArgumentChoices(drivers.primary, c.instanceId) : []),
    [drivers, c?.instanceId],
  );

  const [selected, setSelected] = useState(null);
  const [outcome, setOutcome] = useState(null);

  if (!c) return null;

  const handlePlay = () => {
    if (!selected) return;
    const result = argumentationOutcome(selected, drivers.primary, drivers.secondary);
    setOutcome(result);
  };

  const handleConfirmAfterResult = () => {
    onConfirm(outcome.multiplier, outcome.outcome, selected);
  };

  const handleSkip = () => {
    // Direct acceptance — base price, no risk.
    // No `selected` to memorize since the player explicitly skipped.
    onConfirm(1, 'skipped', null);
  };

  const projected = (c.frequency === 'ponctuel' ? (c.revenue || 0) : (c.dailyRevenue || 0));

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      data-testid="argumentation-modal"
      onClick={onClose}
    >
      <div
        className="bg-ink-800 rounded-xl shadow-2xl border border-ink-600 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-5 py-4 border-b border-ink-600 flex items-start justify-between"
          style={{ background: client ? `linear-gradient(135deg, ${client.color}28, transparent 70%)` : '' }}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <ClientLogo
              client={client}
              clientId={c.clientId}
              category={c.category}
              size="lg"
              withBorder
              className="shadow-lg flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                <Brain className="w-3 h-3" /> Phase de négociation
              </p>
              <h2 className="text-lg font-display font-black text-foreground tracking-tight leading-tight mt-0.5">{c.name}</h2>
              <p className="text-xs text-slate-300 mt-1">
                {c.clientName || '—'} · {c.category} · {formatMoney(projected)}{c.frequency === 'régulier' ? '/j' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-600 hover:text-foreground dark:text-slate-400 dark:hover:text-foreground p-1 rounded transition-colors flex-shrink-0"
            data-testid="argumentation-close"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Skip option (only when commercial hired) */}
          {skipAllowed && !outcome && (
            <div
              className="rounded-md border border-emerald-500/40 bg-emerald-500/10 dark:bg-emerald-500/5 p-3"
              data-testid="argumentation-skip-banner"
            >
              <div className="flex items-start gap-2">
                <Briefcase className="w-4 h-4 text-emerald-700 dark:text-emerald-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-emerald-800 dark:text-emerald-200">Votre commercial s&apos;en occupe</p>
                  <p className="text-[11px] text-slate-300 mt-0.5 leading-relaxed">
                    Votre équipe commerciale (Léa / Marc / Sylvie) gère la négociation. Vous pouvez accepter directement au tarif de base, ou tenter le bonus de +5 % si vous voulez argumenter vous-même.
                  </p>
                  <button
                    onClick={handleSkip}
                    className="text-[11px] mt-2 px-3 py-1.5 rounded-md font-mono font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
                    data-testid="argumentation-skip-btn"
                  >
                    Accepter directement (tarif de base)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tells — visual contextual clues to help the player deduce
              the right argument (Phase 3 bis enrichment, 03/06/2026) */}
          {!outcome && tells.length > 0 && (
            <section data-testid="argumentation-tells">
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> Indices commerciaux
              </h3>
              <div className="grid gap-1.5">
                {tells.map(tell => {
                  const Icon = tell.Icon;
                  return (
                    <div
                      key={tell.key}
                      className={`flex items-start gap-2.5 px-3 py-2 rounded-md border ${
                        tell.weight === 'primary'
                          ? 'border-amber-500/50 bg-amber-100/70 dark:bg-amber-500/5'
                          : 'border-ink-600 bg-slate-100 dark:bg-ink-700/30'
                      }`}
                      data-testid={`tell-${tell.key}`}
                    >
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        tell.weight === 'primary' ? 'text-amber-700 dark:text-amber-300' : 'text-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-[11px] font-bold ${
                          tell.weight === 'primary' ? 'text-amber-900 dark:text-amber-200' : 'text-slate-200'
                        }`}>{tell.label}</p>
                        <p className="text-[10px] text-slate-300 leading-relaxed mt-0.5">{tell.hint}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Argumentation choices */}
          {!outcome && (
            <section data-testid="argumentation-choices">
              <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> Quel argument allez-vous mettre en avant ?
              </h3>
              <div className="grid gap-2">
                {choices.map(arg => {
                  const meta = ARG_META[arg] || ARG_META.prix;
                  const Icon = meta.Icon;
                  const isSelected = selected === arg;
                  return (
                    <button
                      key={arg}
                      type="button"
                      onClick={() => setSelected(arg)}
                      className={`text-left flex items-center gap-3 px-3 py-2.5 rounded-md border transition-all ${
                        isSelected
                          ? 'border-accent bg-accent/15 ring-1 ring-accent/40'
                          : 'border-ink-600 bg-slate-50 dark:bg-ink-700/40 hover:bg-slate-100 dark:hover:bg-ink-700/70 hover:border-ink-500'
                      }`}
                      data-testid={`argumentation-choice-${arg}`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${isSelected ? 'text-accent' : 'text-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-bold ${isSelected ? 'text-foreground' : 'text-slate-200'} capitalize`}>{meta.label}</p>
                        <p className="text-[10px] text-slate-300 mt-0.5">{meta.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-[10px] text-slate-300 italic max-w-md">
                  Choisissez l&apos;angle d&apos;attaque qui correspond le mieux au profil du client. Le bon argument peut faire monter le tarif de +5&nbsp;%.
                </p>
                <button
                  onClick={handlePlay}
                  disabled={!selected}
                  className={`text-xs px-4 py-1.5 rounded-md font-mono font-bold uppercase tracking-wider transition-all inline-flex items-center gap-1.5 flex-shrink-0 ${
                    selected
                      ? 'bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/30'
                      : 'bg-slate-300 dark:bg-ink-700 text-slate-400 cursor-not-allowed'
                  }`}
                  data-testid="argumentation-play-btn"
                >
                  Argumenter
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </section>
          )}

          {/* Outcome */}
          {outcome && (
            <section
              className={`rounded-md border p-4 ${
                outcome.outcome === 'success'
                  ? 'border-emerald-500/50 bg-emerald-100/70 dark:bg-emerald-500/10'
                  : outcome.outcome === 'failure'
                    ? 'border-rose-500/50 bg-rose-100/70 dark:bg-rose-500/10'
                    : 'border-amber-500/50 bg-amber-100/70 dark:bg-amber-500/10'
              }`}
              data-testid={`argumentation-outcome-${outcome.outcome}`}
            >
              <div className="flex items-start gap-3">
                <Sparkles className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  outcome.outcome === 'success' ? 'text-emerald-700 dark:text-emerald-300'
                  : outcome.outcome === 'failure' ? 'text-rose-700 dark:text-rose-300'
                  : 'text-amber-700 dark:text-amber-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${
                    outcome.outcome === 'success' ? 'text-emerald-800 dark:text-emerald-200'
                    : outcome.outcome === 'failure' ? 'text-rose-800 dark:text-rose-200'
                    : 'text-amber-800 dark:text-amber-200'
                  }`}>
                    {outcome.outcome === 'success' && 'Argument retenu ! 🎯'}
                    {outcome.outcome === 'neutral' && 'Argument acceptable.'}
                    {outcome.outcome === 'failure' && 'Mauvais angle.'}
                  </p>
                  <p className="text-xs text-slate-200 leading-relaxed mt-1">{outcome.message}</p>
                  <p className="text-[11px] text-slate-300 mt-2 font-mono">
                    Nouveau tarif : <span className="font-bold text-foreground">{formatMoney(Math.round(projected * outcome.multiplier))}</span>
                    {c.frequency === 'régulier' && '/j'}
                    {outcome.multiplier !== 1 && (
                      <span className={`ml-1 ${outcome.multiplier > 1 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                        ({outcome.multiplier > 1 ? '+' : ''}{Math.round((outcome.multiplier - 1) * 100)}%)
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 italic mt-2">
                    Critère principal attendu : <span className="text-slate-200 font-bold capitalize">{drivers.primary}</span> · secondaire : <span className="text-slate-300 capitalize">{drivers.secondary}</span>.
                  </p>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Actions */}
        {outcome && (
          <div className="px-5 py-3 border-t border-ink-600 bg-slate-100 dark:bg-ink-900/40 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-md font-mono uppercase tracking-wider bg-slate-200 dark:bg-ink-700 text-slate-300 hover:bg-slate-300 dark:hover:bg-ink-600 transition-colors"
              data-testid="argumentation-cancel"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirmAfterResult}
              className="text-xs px-4 py-1.5 rounded-md font-mono font-bold uppercase tracking-wider transition-all inline-flex items-center gap-1.5 bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/30"
              data-testid="argumentation-confirm-btn"
            >
              Valider & assigner
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
